import { type SyntheticEvent, useEffect, useId, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { Trans, t } from '@grafana/i18n';
import { reportInteraction } from '@grafana/runtime';
import { Icon, InlineField, InlineLabel, InlineSwitch, Tooltip, useTheme2 } from '@grafana/ui';
import { type CatalogPlugin } from 'app/features/plugins/admin/types';
import { shouldLoadPluginInFrontendSandbox } from 'app/features/plugins/sandbox/sandboxPluginLoaderRegistry';

import { type SandboxSettingsSpec } from './api/types';
import { useCreateOrUpdateSandboxSettings, useIsSandboxEligible, useSandboxSettings } from './hooks';

export const FrontendSandboxSwitch = ({ plugin }: { plugin: CatalogPlugin }) => {
  const [settings, isLoadingSettings] = useSandboxSettings(plugin.id);
  const [submitData, requestStatus] = useCreateOrUpdateSandboxSettings(plugin.id, Boolean(settings));
  // we make sure we take in consideration the configuration file
  // but the stored setting has priority
  const [defaultEnabled, setDefaultEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isEnabled = requestStatus?.data?.spec?.enabled ?? settings?.enabled ?? defaultEnabled;
  const theme = useTheme2();
  const switchId = useId();
  const label = t('plugins.sandbox.enable-sandbox-label', 'Frontend Sandbox');

  useEffect(() => {
    if (!settings) {
      // default to oss while loading or when there are no settings stored
      shouldLoadPluginInFrontendSandbox({ pluginId: plugin.id }).then(setDefaultEnabled);
    }
  }, [settings, plugin.id]);

  if (isLoadingSettings) {
    return <Skeleton height={40} width={200} />;
  }

  const handleChange = async (event: SyntheticEvent<HTMLInputElement>) => {
    if (requestStatus.isLoading || isLoading) {
      return;
    }
    setIsLoading(true);
    const value = event.currentTarget.checked;
    reportInteraction('plugins_sandbox_switch', {
      plugin: plugin.id,
      enabled: value,
      creator_team: 'grafana_plugins_catalog',
      schema_version: '1.0.0',
    });
    const payload: SandboxSettingsSpec = {
      plugin: plugin.id,
      enabled: value,
      apiAllowList: [],
    };
    await submitData(payload);
    // a browser reload is required for the frontend sandbox
    // to take effect in the plugin
    window.location.reload();
  };

  return (
    <>
      <InlineField
        htmlFor={switchId}
        label={
          <InlineLabel width="auto" htmlFor={switchId}>
            {label}
            <Tooltip interactive content={<FrontendSandboxDeprecationTooltip />} placement="top" theme="info">
              <Icon
                tabIndex={0}
                name="exclamation-triangle"
                size="sm"
                aria-label={t('plugins.sandbox.deprecation-warning-icon-label', 'Deprecation warning')}
                style={{ marginLeft: 10, color: theme.colors.warning.text }}
              />
            </Tooltip>
          </InlineLabel>
        }
        disabled={isLoading}
      >
        <InlineSwitch id={switchId} onChange={handleChange} value={isEnabled} aria-label={label} />
      </InlineField>
    </>
  );
};

export const FrontendSandboxSwitchWrapper = ({ plugin }: { plugin?: CatalogPlugin }) => {
  const isSandboxElegible = useIsSandboxEligible(plugin);
  if (!plugin || !plugin?.id || !isSandboxElegible) {
    return null;
  }

  return <FrontendSandboxSwitch plugin={plugin} />;
};

function FrontendSandboxDeprecationTooltip() {
  const theme = useTheme2();
  return (
    <>
      <Icon name="exclamation-triangle" size="sm" style={{ marginRight: 6, color: theme.colors.warning.text }} />
      <Trans i18nKey="plugins.sandbox.deprecation-warning">
        The Plugin Frontend Sandbox is deprecated and will be removed in Grafana 13.4.
      </Trans>
    </>
  );
}
